import React from "react";
import { Box, Container, Typography, Grid } from "@mui/material";
import { motion } from "framer-motion";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FactoryIcon from "@mui/icons-material/Factory";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const MONO   = "'JetBrains Mono', 'Fira Code', ui-monospace, monospace";
const ACCENT = "#E2684A";
const PURPLE = "#7C3AED";
const CYAN   = "#22D3EE";
const PINK   = "#F472B6";
const BG     = "#030014";
const BG2    = "#0F0728";
const LINE   = "#1E2130";
const INK    = "#E7E9EE";
const INK2   = "#B6BBC6";
const INK3   = "#7D8390";
const R      = "14px";

const industries = [
  { title: "Healthcare & Life Sciences", description: "Accelerate drug discovery, automate patient triage, and ensure HIPAA-compliant data processing.", icon: <LocalHospitalIcon fontSize="large" />, stats: "30% reduction in admin costs", color: ACCENT, colSpan: 8 },
  { title: "Banking & FinTech",           description: "Real-time fraud detection, compliance automation, and risk-aware decision engines.",              icon: <AccountBalanceIcon fontSize="large" />, stats: "<500ms transaction analysis", color: PURPLE, colSpan: 4 },
  { title: "Retail & E-Commerce",         description: "Hyper-personalized shopping recommendations and supply chain forecasting.",                        icon: <ShoppingCartIcon fontSize="large" />,  stats: "25% conversion lift",           color: CYAN,   colSpan: 4 },
  { title: "Manufacturing & Logistics",   description: "Predictive maintenance, route optimization, and autonomous quality control systems.",              icon: <FactoryIcon fontSize="large" />,       stats: "40% downtime reduction",        color: PINK,   colSpan: 8 },
];

const IndustryGrid = () => (
  <Box id="industries" sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
    <Container maxWidth="lg">
      <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55 }}
      >
        <Typography sx={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.16em", textTransform: "uppercase", color: ACCENT, mb: 1.75 }}>
          Vertical Expertise
        </Typography>
        <Typography sx={{
          fontFamily: "'IBM Plex Serif', serif",
          fontSize: { xs: "1.75rem", md: "2.5rem" },
          fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.15,
          color: INK, maxWidth: 720, mb: 6,
        }}>
          Transforming Industries with Precision AI
        </Typography>
      </motion.div>

      <Grid container spacing={2}>
        {industries.map((item, index) => (
          <Grid item xs={12} md={item.colSpan} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: index * 0.07 }}
              style={{ height: "100%" }}
            >
              <Box sx={{
                height: "100%", p: { xs: 3, md: "28px 32px" },
                borderRadius: R, background: BG2, border: `1px solid ${LINE}`,
                display: "flex", flexDirection: "column", justifyContent: "space-between",
                transition: "border-color 0.2s, transform 0.2s",
                "&:hover": { borderColor: item.color, transform: "translateY(-2px)" },
              }}>
                <Box>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: "10px", mb: 2.5,
                    background: `${item.color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: item.color,
                  }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: INK, fontSize: "1.15rem", mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: INK2, fontSize: "14.5px", lineHeight: 1.7 }}>
                    {item.description}
                  </Typography>
                </Box>
                <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${LINE}` }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: item.color, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                      Impact: {item.stats}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

export default IndustryGrid;
